import { useEffect, useRef } from 'react';

import { useEventCallback } from '@gitbook/util-react-hooks';

const SPINE_OVERLAY_SELECTOR = '[data-spine-overlay]';
const UIKIT_POPOVER_SELECTOR = '[data-popover-overlay]';
const UIKIT_PALETTE_SELECTOR = '[data-palette-overlay]';
const TOAST_SELECTOR = '[data-sonner-toast]';
const ANY_POPOVER_SELECTOR = [
    SPINE_OVERLAY_SELECTOR,
    UIKIT_POPOVER_SELECTOR,
    UIKIT_PALETTE_SELECTOR,
].join(',');
const ANY_EXCEPT_SPINE_OVERLAY_SELECTOR = [
    UIKIT_POPOVER_SELECTOR,
    UIKIT_PALETTE_SELECTOR,
    TOAST_SELECTOR,
].join(',');

/**
 * Get the Reflag dialog element.
 */
function getReflagDialogElement(): HTMLDialogElement | null {
    return (
        document
            .getElementById('reflag-feedback-dialog-container')
            ?.shadowRoot?.querySelector('dialog') ?? null
    );
}

/**
 * Check if an element is part of an overlay.
 */
export function checkIsElementContainsInOverlay(element: Element) {
    if (element.closest(ANY_EXCEPT_SPINE_OVERLAY_SELECTOR)) {
        return true;
    }

    if (getReflagDialogElement()?.contains(element)) {
        return true;
    }

    return false;
}

/**
 * Check if a popover is open.
 */
function checkIsPopoverOpen(args: { ignored: Element[] }) {
    const popovers = Array.from(document.querySelectorAll(ANY_POPOVER_SELECTOR)).filter(
        (element) => !args.ignored.includes(element)
    );
    if (popovers.length > 0) {
        return true;
    }
    if (getReflagDialogElement()?.open) {
        return true;
    }
    return false;
}

/**
 * Check if a popover is open and allows to pass a `onClose`
 * callback to be notified when it's closed.
 */
export function useCheckIsPopoverOpen(props: { isDisabled: boolean }) {
    const { isDisabled } = props;
    const mouseUpCleanupRef = useRef<(() => void) | null>(null);
    const ignoredRef = useRef<Element[]>([]);
    useEffect(() => {
        if (!isDisabled) {
            ignoredRef.current = Array.from(document.querySelectorAll(ANY_POPOVER_SELECTOR));
        }
    }, [isDisabled]);
    useEffect(() => {
        if (isDisabled) {
            mouseUpCleanupRef.current?.();
        }
    }, [isDisabled]);
    useEffect(() => () => mouseUpCleanupRef.current?.(), []);
    return useEventCallback((options?: { onClose?: () => void }) => {
        const onClose = options?.onClose;
        if (checkIsPopoverOpen({ ignored: ignoredRef.current })) {
            mouseUpCleanupRef.current?.();

            // When a mouse up event is triggered, we reevaluate if the popover is open.
            const handleMouseUp = () => {
                // Popover is closed, we cleanup the event listener.
                if (!checkIsPopoverOpen({ ignored: ignoredRef.current })) {
                    mouseUpCleanupRef.current?.();
                    onClose?.();
                }
            };
            document.body.addEventListener('mouseup', handleMouseUp);
            mouseUpCleanupRef.current = () => {
                document.body.removeEventListener('mouseup', handleMouseUp);
                mouseUpCleanupRef.current = null;
            };
            return true;
        }
        return false;
    });
}
